import React from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { Button, IconButton } from '@web/ui/components';
import { CancelIcon } from '@web/ui/icons';

const BulkActionContainer = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing('0500')};
  padding-block: ${({ theme }) => theme.spacing('0500')};
  padding-left: ${({ theme }) => theme.spacing('1000')};
  padding-right: ${({ theme }) => theme.spacing('0500')};
  background: ${({ theme }) => theme.color.surfaceContainerHighest};
  border-radius: ${({ theme }) => theme.shape.corner['1000']};
  box-shadow: ${({ theme }) => theme.elevation.level2};
`;

const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  min-width: 100px;
  text-align: center;
  margin-left: -${({ theme }) => theme.spacing('0500')};
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
`;

const Selected = styled.span``;

const SelectAll = styled(Button)`
  box-sizing: border-box;
  font-size: ${({ theme }) => theme.typescale.size['0750']};

  > div {
    padding-inline: ${({ theme }) => theme.spacing('1000')};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

export interface IBulkActionProps {
  selected: number | undefined;
  actions: Array<React.ReactNode>;
  className?: string;
  onSelectAll(): void;
  onClearAll(): void;
}

export const BulkAction = ({
  selected = 0,
  actions,
  className,
  onClearAll,
  onSelectAll,
}: IBulkActionProps) => {
  const t = useTranslations();

  return (
    <BulkActionContainer className={className}>
      <SelectionInfo>
        <IconButton onClick={onClearAll}>
          <CancelIcon size="1000" />
        </IconButton>
        <Selected>{`${selected} ${t('BULK_ACTION_SELECTED')}`}</Selected>
      </SelectionInfo>
      <Actions>
        <SelectAll
          label={t('BULK_ACTION_ALL')}
          onClick={onSelectAll}
          variant="secondary"
          color="onSurface"
          background="surfaceContainerHighest"
        />
        {actions.map((action, index) => (
          <React.Fragment key={index}>{action}</React.Fragment>
        ))}
      </Actions>
    </BulkActionContainer>
  );
};
